/* eslint-disable @typescript-eslint/no-unused-vars */
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import { styled } from '@mui/system'
import {
  useCustomerPopup,
  useMutatePopupInteraction
} from 'hooks/useCompliance'
import { useState } from 'react'
import Markdown from 'components/Markdown'

interface PopUpTitleProps {
  id: string
  children?: React.ReactNode
  onClose: () => void
}

interface PopUpProps {
  onClose?: () => void
  // Direct popup data props
  id?: string | number
  title?: string
  bodyText?: string
  buttonText?: string
  buttonLink?: string
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    margin: theme.spacing(1)
  }
}))

function PopUpTitle(props: PopUpTitleProps) {
  const { onClose, ...other } = props

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={{
          padding: 0,
          position: 'absolute',
          right: 4,
          top: 4,
          color: theme => theme.palette.black.main
        }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  )
}

export default function PopUp({
  onClose,
  id: providedId,
  title: providedTitle,
  bodyText: providedBodyText,
  buttonText: providedButtonText,
  buttonLink: providedButtonLink
}: PopUpProps = {}): JSX.Element | null {
  // *******
  // This now a generic pop-up component that can be used to display any pop-up content retrieved from the Admin site.
  // It can either accept popup data directly as props or query for the first available popup.
  // *******

  const [open, setOpen] = useState<boolean>(true)

  const popupInteractionMutation = useMutatePopupInteraction()

  // Only query if no popup data is provided via props
  const shouldQuery = !providedId && !providedTitle && !providedBodyText
  const { data: queryData } = useCustomerPopup()

  // Use provided props or fall back to query data
  const popupData = shouldQuery
    ? queryData
      ? {
          id: queryData.id,
          title: queryData.title,
          bodyText: queryData.body_text,
          buttonText: queryData.button_text,
          buttonLink: queryData.button_link
        }
      : undefined
    : {
        id: providedId,
        title: providedTitle,
        bodyText: providedBodyText,
        buttonText: providedButtonText,
        buttonLink: providedButtonLink
      }

  const { bodyText, buttonLink, buttonText, id, title } = popupData ?? {}

  function onCreateCustomerPopupInteraction() {
    if (id == null) return
    popupInteractionMutation.mutate(Number(id))
  }

  const handleClose = () => {
    setOpen(false)
    // We still want to record the interaction even if user closes the pop-up without interacting with
    // any of the buttons/links within it.

    onCreateCustomerPopupInteraction()
    onClose?.()
  }

  const handleClickRefer = () => {
    onCreateCustomerPopupInteraction()
    // If there's a buttonLink and it's not the root path, open it in a new tab
    if (buttonLink && buttonLink !== '/') {
      window.open(buttonLink, '_blank')
    } else {
      // If no buttonLink or root path, just close the popup
      setOpen(false)
      onClose?.()
    }
  }

  return popupData ? (
    <BootstrapDialog open={open} disableScrollLock>
      <PopUpTitle id='popup-title' onClose={handleClose} />
      <DialogContent
        sx={{
          p: 2, // 16px padding
          '&::-webkit-scrollbar': {
            width: 0,
            height: 0
          }
        }}
      >
        <Typography gutterBottom textAlign='center' variant='h4' component='h2'>
          {title}
        </Typography>

        <Markdown content={bodyText || ''} />
      </DialogContent>

      {buttonText && (
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button variant='outlined' onClick={handleClickRefer} fullWidth>
            {buttonText}
          </Button>
        </DialogActions>
      )}
    </BootstrapDialog>
  ) : null
}
